import { createColumnHelper } from "@tanstack/react-table";
import actionIcon from "assets/svg/action.svg";
import deleteIcon from "assets/svg/delete.svg";
import plusIcon from "assets/svg/plus-icon.svg";
import DataTable from "components/datatable/datatable";
import Loader from "components/loader";
import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "react-modal";
import "./style.css";

import {
  convertToDDMMYYYYWithTime,
  formatDateToCustomFormat,
} from "utils/utils";
import { deleteliveEvents, getAllLiveEvents } from "../../../axios/api";

const LiveEvents = () => {
  const navigate = useNavigate();
  const [searchField, setSearchField] = useState("");

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    filter: {},
  });

  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [liveEventsData, setLiveEventsData] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const columnHelper = createColumnHelper<any>();

  const columns: any = [
    columnHelper.accessor("title", {
      id: "title",
      header: () => (
        <p className="text-base font-normal text-[#424242] dark:text-white">
          Event Title
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 flex items-center  text-sm font-bold text-navy-700 dark:text-white">
            {info && info?.row?.original?.thumbnail_url ? (
              <img
                src={info.row.original?.thumbnail_url}
                alt="cover"
                className="me-3 w-8"
              />
            ) : null}
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("url", {
      id: "url",
      header: () => (
        <p className="text-base font-normal text-[#424242] dark:text-white">
          Event URL
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 flex items-center  text-sm font-bold text-blue-600 underline dark:text-white">
            <a href={info.getValue()} target="_blank" rel="noreferrer">
              {info.getValue().slice(0, 20)}
            </a>
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("start_time", {
      id: "start_time",
      header: () => (
        <p className="text-base font-normal text-[#424242] dark:text-white">
          Start Date
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 flex items-center  text-sm font-bold text-navy-700 dark:text-white">
            {convertToDDMMYYYYWithTime(
              info.getValue()?.slice(0, 10) +
                " " +
                info.getValue()?.slice(11, 19)
            )}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("end_time", {
      id: "end_time",
      header: () => (
        <p className="text-base font-normal text-[#424242] dark:text-white">
          End Date
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 flex items-center  text-sm font-bold text-navy-700 dark:text-white">
            {convertToDDMMYYYYWithTime(
              info.getValue()?.slice(0, 10) +
                " " +
                info.getValue()?.slice(11, 19)
            )}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <p className="px-[3rem] text-base font-normal text-[#424242] dark:text-white">
          Action
        </p>
      ),
      cell: (info) => (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              navigate(`/admin/live-events/edit`, { state: info.getValue() })
            }
            className="text-black me-2 inline-flex items-center rounded-lg border-4 px-3 py-2.5 text-center text-sm font-medium hover:bg-[#0098DA] hover:text-white focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            <img
              src={actionIcon}
              alt="action-icon"
              className="w-4.1 me-2 h-5"
            />
            Edit
          </button>
          <button
            onClick={() => openModal(info.row.original.id)}
            type="button"
            className="text-black me-2 inline-flex items-center rounded-lg border-4 px-3 py-2.5 text-center text-sm font-medium hover:bg-[#0098DA] hover:text-white focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            <img
              src={deleteIcon}
              alt="delete-icon"
              className="w-4.1 me-2 h-5"
            />
            Delete
          </button>
        </div>
      ),
    }),
  ];

  useEffect(() => {
    const fetchLiveEvents = async () => {
      try {
        setLoading(true);
        const liveEventDataResp = await getAllLiveEvents(
          filter.page,
          filter.limit
        );
        setLiveEventsData(liveEventDataResp?.data?.data?.live_videos);
        setTotalCount(liveEventDataResp?.data?.data?.totalResults);
        if (!liveEventDataResp?.data?.success) {
          toast.error("Error Fetching Live Events");
          console.log("liveEventDataResp", liveEventDataResp);
        }
      } catch (error) {
        console.log("error", error);
        toast.error("Error Fetching Live Events");
      } finally {
        setLoading(false);
      }
    };
    fetchLiveEvents();
  }, [filter]);

  const onPageChange = ({ selected }: any) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      page: selected + 1,
    }));
  };

  const openModal = (id: string) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteliveEvents(deleteId);
        toast.success("Live-Event deleted successfully");
        setFilter((prev) => ({ ...prev, page: 1 }));
      } catch (error) {
        toast.error("Error deleting Live-Event");
      } finally {
        closeModal();
      }
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="h-full rounded-md bg-white">
        <div className="flex justify-between p-4">
          <span className="text-2xl font-bold">Upcoming Live Events</span>
          <div className="relative ml-[5rem]">
            {/* <img
              src={searchIcon}
              className="absolute left-3 top-[10px]"
              alt="search-icon"
            ></img> */}
            {/* <input
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  setFilter((prev) => {
                    let updatedFilter = { ...prev };
                    if (searchField === "") {
                      updatedFilter = { ...updatedFilter, filter: {} };
                      return updatedFilter;
                    }
                    updatedFilter = {
                      ...updatedFilter,
                      filter: {
                        ...updatedFilter.filter,
                        search: searchField,
                      },
                    };
                    return updatedFilter;
                  });
                }
              }}
              onChange={(e) => setSearchField(e.target.value.trim())}
              className=" w-60 rounded bg-[#E5F6FF] py-1 pl-10"
              placeholder="Search"
              type="search"
            /> */}
            <div>
              <Link
                to={`/admin/live-events/add`}
                className="flex items-center justify-center gap-2 rounded-sm bg-[#0098DA] px-4 py-2 text-base font-bold text-white"
              >
                <img
                  src={plusIcon}
                  className="my-auto inline"
                  alt="plus-icon"
                ></img>
                Events
              </Link>
            </div>
          </div>
        </div>
        <div>
          {liveEventsData && liveEventsData.length > 0 ? (
            <DataTable columns={columns} tableData={liveEventsData} />
          ) : (
            <div className="flex min-h-[80vh] items-center justify-center text-4xl font-bold">
              {!loading ? "No Upcoming Live Events Found" : ""}
            </div>
          )}
        </div>
        <ReactPaginate
          className="pagination"
          previousLabel={"<<"}
          nextLabel={">>"}
          breakLabel={"..."}
          pageCount={Math.ceil(totalCount / filter.limit)}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={onPageChange}
          containerClassName={"pagination"}
          activeClassName={"active"}
          pageClassName={"page"}
          previousClassName={"previous"}
          nextClassName={"next"}
          breakClassName={"break"}
          disabledClassName={"disabled"}
        />
      </div>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Confirm Deletion"
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Are you sure you want to delete this Live Event?</h2>
        <div className="modal-buttons">
          <button onClick={handleDelete} className="confirm-button">
            Yes
          </button>
          <button onClick={closeModal} className="cancel-button">
            No
          </button>
        </div>
      </Modal>
    </>
  );
};

export default LiveEvents;
